import { useEffect } from 'react';
import { CheckPreferenceResponse } from '@weave/schema-gen-ts/dist/schemas/comm-preference/preference/v1/preference_service.pb';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { ScheduledSms } from '@weave/schema-gen-ts/dist/schemas/messaging/scheduled/shared/v1/models.pb';
import {
  MessageType_Enum,
  OutboundMessageStatus,
} from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { MediaQueries } from '@frontend/api-media';
import { SMSDataHooks, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { StateRefHelpers } from '@frontend/state-ref-helpers';
import { useCheckSMSPreference } from '../comm-preference-queries';
import { ThreadFocusedSmsData, ThreadMetaData } from '../types';

const FILTERED_SCHEDULED_STATUSES: readonly OutboundMessageStatus[] = [
  OutboundMessageStatus.DELETED,
  OutboundMessageStatus.DELIVERED,
  OutboundMessageStatus.SENT,
];

type UseThreadArgs = {
  threadId: string;
  groupId: string;
  providedPersonPhone?: string;
  focusedSms?: ThreadFocusedSmsData;
  isNew?: boolean;
  getThreadRetryLimit?: number;
  disable?: boolean;
};

type UseThreadReturnType = {
  threadItems: ThreadItem[];
  isLoadingFirstPage: boolean;
  isFetching: boolean;
  hasError: boolean;
  hasOlderMessages: boolean;
  fetchOlderMessages: () => void;
  hasNewerMessages: boolean;
  fetchNewerMessages: () => void;
  scheduledMessages: ScheduledSms[];
  metadata?: ThreadMetaData;
  smsPreference?: CheckPreferenceResponse;
  personPhone?: string;
  mediaQueries: Record<string, MediaQueries.UseMmsMediaItem>;
  refetchThread: () => void;
};

export const useThread = ({
  threadId,
  groupId,
  providedPersonPhone,
  focusedSms,
  isNew,
  getThreadRetryLimit,
  disable = false,
}: UseThreadArgs): UseThreadReturnType => {
  const [previousIsNew, currentIsNew] = StateRefHelpers.usePreviousValue(isNew);

  const { getThreadPersonId, getThreadPersonPhone, isSmsMessageItem } = SMSDataSharedUtils.threadItemsUtils;

  const { getThreadQuery, threadItems } = SMSDataHooks.useGetThreadQueryData({
    threadId,
    locationId: groupId,
    focusItemId: focusedSms?.id,
    focusItemCreatedAt: focusedSms?.createdAt,
    enabled: !!threadId && !!groupId && !disable && !currentIsNew,
    retryLimit:
      getThreadRetryLimit !== undefined
        ? getThreadRetryLimit
        : (failureCount) => (currentIsNew ? false : failureCount < 3),
    cacheTime: 0, // Invalidate when unused
  });

  const scheduledMessagesQuery = ManualSmsScheduledV1.Queries.useGetThreadQuery({
    request: {
      threadId,
      locationId: groupId,
    },
    options: {
      enabled: !!threadId && !!groupId && !disable && !currentIsNew,
      retry:
        getThreadRetryLimit !== undefined
          ? getThreadRetryLimit
          : (failureCount) => (currentIsNew ? false : failureCount < 3),
      select: (data) => {
        const seen = new Set<string>();
        const uniqueScheduledSmss = data.scheduledSmss.filter((sms) => {
          if (seen.has(sms.id)) return false;
          seen.add(sms.id);
          return true;
        });
        return {
          ...data,
          scheduledSmss: uniqueScheduledSmss.filter(
            (sms) => sms.status === OutboundMessageStatus.SCHEDULED || sms.status === OutboundMessageStatus.PAUSED
          ),
        };
      },
    },
  });

  const metadataPageThread = getThreadQuery.data?.pages?.at(0)?.thread;
  const mostRecentSmsItem = metadataPageThread?.items?.find(isSmsMessageItem);

  const metadata: UseThreadReturnType['metadata'] =
    metadataPageThread && 'id' in metadataPageThread
      ? {
          ...metadataPageThread,
          person:
            metadataPageThread?.person ??
            (mostRecentSmsItem
              ? { personId: getThreadPersonId(mostRecentSmsItem), firstName: '', lastName: '', preferredName: '' } // Handles inboundAvatar logic in threadContext.
              : undefined),
        }
      : undefined;
  const personPhone = mostRecentSmsItem ? getThreadPersonPhone(mostRecentSmsItem) : providedPersonPhone;
  const formattedPersonPhone = formatPhoneNumberE164(personPhone || '');

  const { data: smsPreference } = useCheckSMSPreference({
    userChannelAddress: formattedPersonPhone || '',
    channel: Channel.CHANNEL_SMS,
    locationId: groupId,
    messageType: MessageType_Enum.MESSAGING_MANUAL,
  });

  const threadMediaIds =
    getThreadQuery.data?.pages
      .flatMap((page) => page.thread.items)
      .filter(isSmsMessageItem)
      .flatMap((item) => item.smsMessage.media.map((mediaItem) => mediaItem.mediaId)) ?? [];
  const filteredScheduledMessages =
    scheduledMessagesQuery.data?.scheduledSmss.filter(
      (message) => !FILTERED_SCHEDULED_STATUSES.includes(message.status)
    ) ?? [];
  const scheduledMediaIds = scheduledMessagesQuery.data?.scheduledSmss.flatMap((message) => message.mediaIds) ?? [];
  const mediaIds = [...threadMediaIds, ...scheduledMediaIds];
  const mediaQueries = MediaQueries.useMmsMedia({ mediaIds, locationId: groupId });
  const transformedMediaQueries = mediaIds.reduce<Record<string, MediaQueries.UseMmsMediaItem>>((acc, curr) => {
    const query = mediaQueries.find((query) => query.data?.mediaId === curr);
    if (query) acc[curr] = query;
    return acc;
  }, {});

  useEffect(() => {
    if (previousIsNew && !currentIsNew) {
      getThreadQuery.refetch();
      scheduledMessagesQuery.refetch();
    }
  }, [currentIsNew]);

  return {
    threadItems,
    isLoadingFirstPage: !isNew && (getThreadQuery.isLoading || scheduledMessagesQuery.isLoading),
    isFetching: getThreadQuery.isFetching || scheduledMessagesQuery.isFetching,
    hasError: getThreadQuery.isError,
    hasOlderMessages: !!getThreadQuery.hasNextPage,
    fetchOlderMessages: getThreadQuery.fetchNextPage,
    hasNewerMessages: !!getThreadQuery.hasPreviousPage,
    fetchNewerMessages: getThreadQuery.fetchPreviousPage,
    scheduledMessages: filteredScheduledMessages.sort((a, b) => (b.sendAt < a.sendAt ? 1 : -1)),
    metadata: threadId ? metadata : undefined,
    personPhone,
    smsPreference,
    mediaQueries: transformedMediaQueries,
    refetchThread: getThreadQuery.refetch,
  };
};
